// src/pages/organiser/BulkUploadPage.js
// AI reads pasted text or uploaded photo → extracts multiple events → organiser reviews → publish all

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { createEvent } from '../../lib/db';
import { storage, callExtractBulkEvents } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const STOCK_IMAGES = {
  'music':     'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80',
  'sport_gaa': 'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&q=80',
  'sport':     'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80',
  'kids':      'https://images.unsplash.com/photo-1472162072942-cd5147eb3902?w=800&q=80',
  'community': 'https://images.unsplash.com/photo-1511632765153-994ad668debb?w=800&q=80',
  'markets':   'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=800&q=80',
  'arts':      'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=800&q=80',
  'health':    'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80',
  'farming':   'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=800&q=80',
  'food':      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  'education': 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80',
  'social':    'https://images.unsplash.com/photo-1529543544282-ea669407fca3?w=800&q=80',
};

const CATEGORY_LABELS = {
  'music':     'Music & Dance',
  'sport_gaa': 'Sport — GAA',
  'sport':     'Sport — Other',
  'kids':      'Kids & Family',
  'community': 'Community',
  'markets':   'Markets',
  'arts':      'Arts & Culture',
  'health':    'Health & Wellbeing',
  'farming':   'Farming & Equine',
  'food':      'Food & Drink',
  'education': 'Education & Workshops',
  'social':    'Social',
};

const BulkUploadPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [step, setStep] = useState('input'); // 'input' | 'processing' | 'review' | 'publishing' | 'done'
  const [inputText, setInputText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [dataFile, setDataFile] = useState(null);
  const [events, setEvents] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [imageOutcome, setImageOutcome] = useState(null); // 'attached' | 'failed' | 'none'

  const readDataFileAsText = async (file) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) {
      return await file.text();
    }
    // .xlsx / .xls — binary spreadsheet, needs parsing before it's readable text
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_csv(firstSheet);
  };

  const extractEvents = async () => {
    if (!inputText.trim() && !imageFile && !dataFile) {
      toast.error('Please paste some text, upload a photo, or upload a file');
      return;
    }

    setStep('processing');

    try {
      let payload;

      if (imageFile) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(imageFile);
        });
        payload = { imageBase64: base64, imageMediaType: imageFile.type };
      } else if (dataFile) {
        const text = await readDataFileAsText(dataFile);
        payload = { text };
      } else {
        payload = { text: inputText };
      }

      const result = await callExtractBulkEvents(payload);
      const parsed = result.data?.events || [];
      setEvents(parsed.map((e, i) => ({ ...e, id: i, selected: true })));
      setStep('review');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not read the content — please try again');
      setStep('input');
    }
  };

  const toggleEvent = (id) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, selected: !e.selected } : e));
  };

  const updateEvent = (id, field, value) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const publishAll = async () => {
    const selected = events.filter(e => e.selected);
    if (selected.length === 0) { toast.error('Select at least one event'); return; }

    setPublishing(true);
    setStep('publishing');

    // If this batch came from an uploaded photo, upload it once to Storage
    // and use it for every event extracted from it.
    let uploadedImageUrl = null;
    let imageUploadFailed = false;
    if (imageFile) {
      try {
        const imgRef = ref(storage, `events/${user.uid}/${Date.now()}`);
        await uploadBytes(imgRef, imageFile);
        uploadedImageUrl = await getDownloadURL(imgRef);
      } catch (err) {
        console.error('Photo upload failed, falling back to stock image:', err);
        imageUploadFailed = true;
      }
    }

    let published = 0;
    for (const event of selected) {
      try {
        let eventDate = new Date();
        if (event.date) {
          const parts = event.date.split('/');
          if (parts.length === 3) {
            eventDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          }
        }

        const category = event.category || 'community';
        const imageUrl = uploadedImageUrl || STOCK_IMAGES[category] || STOCK_IMAGES['community'];

        await createEvent({
          title:         event.title,
          description:   event.description || '',
          date:          eventDate,
          nextDate:      eventDate,
          time:          event.time || 'See details',
          venue:         event.venue || '',
          category,
          imageUrl,
          eventCharge:   event.charge || 'Free',
          eventType:     (event.charge && event.charge !== 'Free') ? 'paid' : 'free',
          repeat:        'once',
          price:         null,
          townId:        profile?.defaultTownId || localStorage.getItem('gather-town-id'),
          organiserId:   user.uid,
          // Prefer this specific event's own extracted contact details
          // (e.g. a poster with its own organiser name/phone/email) over
          // the uploader's saved profile — critical when one account
          // (a community hub page) is posting on behalf of several real,
          // different organisers. organiserId stays the uploader's own
          // account regardless, since that's what controls edit access
          // in Gather — only the *displayed* name/contact should reflect
          // the real organiser from the source material.
          organiserName: event.organiserName || profile?.displayName || user.email,
          organiserPhone: event.organiserPhone || profile?.organiserPhone || null,
          organiserEmail: event.organiserEmail || profile?.organiserEmail || null,
          bookingUrl:    profile?.bookingUrl || null,
          status:        'published',
          source:        'bulk_upload',
        });
        published++;
      } catch (err) {
        console.error('Failed to publish:', event.title, err);
      }
    }

    if (imageUploadFailed) {
      setImageOutcome('failed');
      toast.error('Published, but your photo didn\'t upload — add it manually via My Events → Edit for each event', { duration: 7000 });
    } else if (!imageFile) {
      setImageOutcome('none');
      toast.success(`${published} event${published !== 1 ? 's' : ''} published!`);
    } else {
      setImageOutcome('attached');
      toast.success(`${published} event${published !== 1 ? 's' : ''} published — with your photo attached!`);
    }
    setStep('done');
    setPublishing(false);
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 40 }}>

      {/* Header */}
      <div style={{ padding: '52px 20px 20px' }}>
        <button onClick={() => navigate('/organiser')} style={s.back}>
          <div style={s.backCircle}>←</div>
          <span style={s.backLbl}>My events</span>
        </button>
        <h1 style={s.h1}>Add multiple events</h1>
        <p style={s.sub}>Paste text, upload a photo, or upload a spreadsheet — AI extracts all events at once</p>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* STEP: INPUT */}
        {step === 'input' && (
          <>
            {/* Text input */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Option 1 — Paste text</div>
              <textarea
                style={{ ...s.textarea }}
                value={inputText}
                onChange={e => { setInputText(e.target.value); setImageFile(null); setDataFile(null); }}
                placeholder="Paste a programme, email, Facebook post, WhatsApp message — anything describing your events..."
                rows={8}
              />
            </div>

            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', fontWeight: 600, margin: '8px 0' }}>or</div>

            {/* Photo input */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Option 2 — Upload a photo</div>
              <label style={s.photoUpload}>
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB — try a smaller image or take it a bit further back'); return; }
                    setImageFile(file); setInputText(''); setDataFile(null);
                  }} />
                {imageFile
                  ? <span style={{ color: 'var(--sage-deep)', fontWeight: 700 }}>✓ {imageFile.name}</span>
                  : <span>📷 Tap to upload a photo of a poster, programme or handwritten list</span>
                }
              </label>
            </div>

            <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', fontWeight: 600, margin: '8px 0' }}>or</div>

            {/* Spreadsheet input */}
            <div style={s.section}>
              <div style={s.sectionLabel}>Option 3 — Upload a spreadsheet</div>
              <label style={s.photoUpload}>
                <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5MB'); return; }
                    setDataFile(file); setInputText(''); setImageFile(null);
                  }} />
                {dataFile
                  ? <span style={{ color: 'var(--sage-deep)', fontWeight: 700 }}>✓ {dataFile.name}</span>
                  : <span>📊 Tap to upload a CSV or Excel file of your events</span>
                }
              </label>
            </div>

            <button onClick={extractEvents} style={s.primaryBtn}>
              Extract events with AI ✨
            </button>
          </>
        )}

        {/* STEP: PROCESSING */}
        {step === 'processing' && (
          <div style={s.centreCard}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✨</div>
            <div style={s.h2}>Reading your content...</div>
            <p style={s.bodyText}>AI is extracting all the events. This takes a few seconds.</p>
            <div style={s.spinner} />
          </div>
        )}

        {/* STEP: REVIEW */}
        {step === 'review' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={s.h2}>{events.length} event{events.length !== 1 ? 's' : ''} found</div>
              <button onClick={() => setStep('input')} style={s.ghostBtn}>Start over</button>
            </div>

            {events.length === 0 ? (
              <div style={s.centreCard}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🤔</div>
                <div style={s.h2}>No events found</div>
                <p style={s.bodyText}>Try adding more detail — dates, times and venue names help AI extract events accurately.</p>
                <button onClick={() => setStep('input')} style={s.primaryBtn}>Try again</button>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
                  Tick the events you want to publish. Tap any field to edit.
                </p>

                {events.map(event => (
                  <div key={event.id} style={{ ...s.eventCard, opacity: event.selected ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                      <input type="checkbox" checked={event.selected} onChange={() => toggleEvent(event.id)}
                        style={{ marginTop: 4, width: 18, height: 18, accentColor: 'var(--sage-deep)', flexShrink: 0 }} />
                      <input
                        style={s.inlineInput}
                        value={event.title}
                        onChange={e => updateEvent(event.id, 'title', e.target.value)}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8, paddingLeft: 30 }}>
                      <div>
                        <div style={s.miniLabel}>Date</div>
                        <input style={s.miniInput} value={event.date || ''} placeholder="DD/MM/YYYY"
                          onChange={e => updateEvent(event.id, 'date', e.target.value)} />
                      </div>
                      <div>
                        <div style={s.miniLabel}>Time</div>
                        <input style={s.miniInput} value={event.time || ''}
                          onChange={e => updateEvent(event.id, 'time', e.target.value)} />
                      </div>
                    </div>

                    <div style={{ paddingLeft: 30 }}>
                      <div style={s.miniLabel}>Venue</div>
                      <input style={{ ...s.miniInput, width: '100%', marginBottom: 8 }} value={event.venue || ''}
                        onChange={e => updateEvent(event.id, 'venue', e.target.value)} />

                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={s.miniLabel}>Category</div>
                          <select style={s.miniInput} value={event.category || 'community'}
                            onChange={e => updateEvent(event.id, 'category', e.target.value)}>
                            {Object.entries(CATEGORY_LABELS).map(([val, lbl]) => (
                              <option key={val} value={val}>{lbl}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ width: 80 }}>
                          <div style={s.miniLabel}>Charge</div>
                          <input style={s.miniInput} value={event.charge || 'Free'}
                            onChange={e => updateEvent(event.id, 'charge', e.target.value)} />
                        </div>
                      </div>

                      {(event.organiserName || event.organiserPhone || event.organiserEmail) && (
                        <div style={{ marginTop: 10, background: 'var(--cream)', borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--sage-deep)', marginBottom: 6 }}>
                            ℹ️ Organiser found in this content — check it's right
                          </div>
                          <input style={{ ...s.miniInput, width: '100%', marginBottom: 6 }}
                            placeholder="Organiser name" value={event.organiserName || ''}
                            onChange={e => updateEvent(event.id, 'organiserName', e.target.value)} />
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input style={{ ...s.miniInput, flex: 1 }}
                              placeholder="Phone" value={event.organiserPhone || ''}
                              onChange={e => updateEvent(event.id, 'organiserPhone', e.target.value)} />
                            <input style={{ ...s.miniInput, flex: 1 }}
                              placeholder="Email" value={event.organiserEmail || ''}
                              onChange={e => updateEvent(event.id, 'organiserEmail', e.target.value)} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <button onClick={publishAll} disabled={publishing} style={{ ...s.primaryBtn, marginTop: 16 }}>
                  Publish {events.filter(e => e.selected).length} event{events.filter(e => e.selected).length !== 1 ? 's' : ''} →
                </button>
              </>
            )}
          </>
        )}

        {/* STEP: PUBLISHING */}
        {step === 'publishing' && (
          <div style={s.centreCard}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>📅</div>
            <div style={s.h2}>Publishing your events...</div>
            <p style={s.bodyText}>Hang on a moment.</p>
          </div>
        )}

        {/* STEP: DONE */}
        {step === 'done' && (
          <div style={s.centreCard}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🎉</div>
            <div style={s.h2}>All done!</div>
            <p style={s.bodyText}>Your events are now live on Gather.</p>

            {(imageOutcome === 'none' || imageOutcome === 'failed') && (
              <div style={{ background: 'var(--cream)', borderRadius: 14, padding: '16px', textAlign: 'left', marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1A2E1A', marginBottom: 4 }}>
                  {imageOutcome === 'failed' ? '📷 Your photo didn\'t attach' : '📷 No photo for these ones'}
                </div>
                <div style={{ fontSize: 13, color: '#4A6A4A', lineHeight: 1.5 }}>
                  {imageOutcome === 'failed'
                    ? 'Something went wrong uploading your photo, so these are using a stock image for now.'
                    : 'These were added from text, so they\'re using a stock image for now.'}
                  {' '}Got a poster or photo for any of them? Go to My Events → tap the event → Edit → add the photo there.
                </div>
              </div>
            )}

            <button onClick={() => navigate('/organiser')} style={s.primaryBtn}>
              Back to my events
            </button>
          </div>
        )}
      </div>
      <PageBack position="bottom" fallbackPath="/organiser" />
    </div>
  );
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#2C4A2E', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: '#4A6A4A', fontWeight: 600 },
  h1: { fontSize: 26, fontWeight: 800, color: '#1A2E1A', marginBottom: 6 },
  h2: { fontSize: 20, fontWeight: 800, color: '#1A2E1A', marginBottom: 8 },
  sub: { fontSize: 14, color: '#4A6A4A', fontWeight: 500 },
  section: { marginBottom: 16 },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#2C4A2E', marginBottom: 8 },
  textarea: { width: '100%', padding: '14px', borderRadius: 14, border: '1.5px solid #C8D9C9', fontSize: 14, fontFamily: 'Nunito, sans-serif', color: '#1A2E1A', background: 'var(--white)', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  photoUpload: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--white)', border: '1.5px dashed #C8D9C9', borderRadius: 14, padding: '24px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#2C4A2E', textAlign: 'center', lineHeight: 1.5 },
  primaryBtn: { width: '100%', background: 'var(--sage-deep)', color: 'white', border: 'none', borderRadius: 16, padding: 17, fontSize: 16, fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', marginTop: 8 },
  ghostBtn: { background: 'none', border: '1.5px solid #C8D9C9', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', color: '#4A6A4A' },
  centreCard: { background: 'var(--white)', borderRadius: 18, padding: 32, textAlign: 'center', boxShadow: 'var(--shadow-sm)' },
  bodyText: { fontSize: 14, color: '#4A6A4A', lineHeight: 1.6, marginBottom: 20 },
  eventCard: { background: 'var(--white)', borderRadius: 16, padding: '16px', marginBottom: 12, boxShadow: 'var(--shadow-sm)', transition: 'opacity 0.2s' },
  inlineInput: { flex: 1, fontSize: 15, fontWeight: 700, color: '#1A2E1A', border: 'none', borderBottom: '1.5px solid #C8D9C9', background: 'transparent', fontFamily: 'Nunito, sans-serif', outline: 'none', width: '100%', padding: '2px 0' },
  miniLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#7A9A7A', marginBottom: 4 },
  miniInput: { width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #C8D9C9', fontFamily: 'Nunito, sans-serif', background: 'var(--white)', color: '#1A2E1A', boxSizing: 'border-box', outline: 'none' },
  spinner: { width: 32, height: 32, border: '3px solid #C8D9C9', borderTop: '3px solid var(--sage-deep)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '16px auto 0' },
};

export default BulkUploadPage;
